import React, { useEffect, useState } from "react";
import { FaCalendarAlt, FaHistory, FaPlus, FaTachometerAlt, FaChevronRight, FaChevronDown, FaSignOutAlt, FaCog, FaUserTie, FaQuestionCircle, FaList, FaChartBar, FaBroadcastTower } from "react-icons/fa";
import { FaGift, FaUser, FaUsers, FaWallet, FaKeyboard } from "react-icons/fa6";
import { IoIosCreate } from "react-icons/io";
import { RiListOrdered, RiUserAddFill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { Link, Outlet } from "react-router-dom";
import HandleLogout from "../hooks/handleLogout";
import { setTypingMode } from "../store/activePage";
import CustomModal from "../components/modal";

const ACCENT = "#14b8a6";
const ACCENT_SOFT = "#7dd3c7";
const TYPING_MODE_OPTIONS = [
  { value: "tanglish", label: "Tanglish -> Tamil" },
  { value: "english", label: "English" },
];

const OperatorDashboard = () => {
  const { headerName, activeMenu } = useSelector((state) => state.activePage);
  const [showSubMenu, setShowSubMenu] = useState({ customer: false });
  const [language, setLanguage] = useState("en");
  const [orgName, setOrgName] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const dispatch = useDispatch();
  const typingMode = useSelector(
    (state) => state.activePage.typingMode || "tanglish",
  );

  const toggleMenu = (menu) =>
    setShowSubMenu((prev) => ({ ...prev, [menu]: !prev[menu] }));

  useEffect(() => {
    const org = localStorage.getItem("org-name");
    setOrgName(org);
    const match = document.cookie.match(/googtrans=\/en\/([^;]+)/);
    setLanguage(match?.[1] || "en");
  }, []);

  const handleLanguageChange = (newLang) => {
    const cookieValue = `/en/${newLang}`;
    const domain = window.location.hostname;
    document.body.classList.add("switching-language");
    document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
    const hostParts = domain.split(".");
    if (hostParts.length > 2) {
      const rootDomain = hostParts.slice(-2).join(".");
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${rootDomain}`;
    }
    document.cookie = `googtrans=${cookieValue}; path=/`;
    document.cookie = `googtrans=${cookieValue}; path=/; domain=${domain}`;
    setTimeout(() => window.location.reload(), 300);
  };

  const TypingModeSelector = () => (
    <div className="flex items-center gap-1 rounded-2xl bg-slate-100 p-1 ring-1 ring-slate-200">
      {TYPING_MODE_OPTIONS.map((option) => {
        const isActive = typingMode === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => dispatch(setTypingMode(option.value))}
            className={`rounded-xl px-3 py-2 text-[11px] font-black transition-all ${
              isActive
                ? "bg-teal-600 text-white shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );

  const logo = require("../assets/images/logo.jpeg");

  return (
    <>
      <style>{`
        .op-sidebar-scroll::-webkit-scrollbar { width: 4px; }
        .op-sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
        .op-sidebar-scroll::-webkit-scrollbar-thumb { background: rgba(20,184,166,0.35); border-radius: 99px; }
        .op-nav-link { transition: all 0.2s ease; }
        .op-nav-link:hover { background: rgba(20,184,166,0.12); }
        .op-nav-active { background: rgba(20,184,166,0.16); border-left: 3px solid #14b8a6; }
        .op-main-scroll::-webkit-scrollbar { width: 6px; }
        .op-main-scroll::-webkit-scrollbar-track { background: #edf4f7; }
        .op-main-scroll::-webkit-scrollbar-thumb { background: #bfd0db; border-radius: 99px; }
        .op-submenu-enter { animation: submenuOpen 0.2s ease both; }
        @keyframes submenuOpen {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="flex h-screen overflow-hidden bg-[#eef4f8]">
        {/* ── Sidebar ─────────────────────────────────────────────────────── */}
        <aside
          style={{
            width: collapsed ? "72px" : "268px",
            background:
              "linear-gradient(180deg, #0f172a 0%, #132238 48%, #0f1d30 100%)",
            transition: "width 0.3s cubic-bezier(0.4,0,0.2,1)",
            borderRight: "1px solid rgba(20,184,166,0.14)",
            boxShadow: "8px 0 32px rgba(15,23,42,0.2)",
          }}
          className="flex flex-col h-full shrink-0 overflow-hidden"
        >
          {/* Logo area */}
          <div
            style={{ borderBottom: "1px solid rgba(125,211,199,0.12)" }}
            className="flex items-center gap-3 px-4 py-4 shrink-0"
          >
            <div className="relative shrink-0">
              <img
                src={logo}
                className="w-10 h-10 rounded-xl object-cover ring-2 ring-teal-400/30"
                alt="logo"
              />
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 border-2 border-[#141B2D] rounded-full" />
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <p className="text-white font-black text-sm tracking-wide leading-tight truncate">
                  Digital Moi Tech
                </p>
                <p className="text-teal-300/80 text-xs font-medium truncate">
                  Enterprise Dashboard
                </p>
              </div>
            )}
            <button
              onClick={() => setCollapsed((p) => !p)}
              style={{ marginLeft: "auto", color: "rgba(226,232,240,0.35)" }}
              className="shrink-0 hover:text-teal-300 transition-colors p-1"
            >
              <FaChevronRight
                style={{
                  transform: collapsed ? "rotate(0deg)" : "rotate(180deg)",
                  transition: "transform 0.3s ease",
                }}
                className="text-xs"
              />
            </button>
          </div>

          {/* Nav */}
          <nav className="flex-1 overflow-y-auto op-sidebar-scroll px-2 py-3 space-y-0.5">
            {[
              { to: "/dashboard/home",            menu: "home",            icon: FaTachometerAlt, label: "Dashboard" },
              { to: "/dashboard/live-tracker",     menu: "liveTracker",     icon: FaBroadcastTower,label: "Live Tracker" },
              { to: "/dashboard/create-new-event",menu: "createEvent",     icon: IoIosCreate,     label: "Create New Event" },
              { to: "/dashboard/old-record-upload",menu:"oldRecordUpload", icon: FaPlus,          label: "Add Old Record" },
              { to: "/dashboard/event-list",       menu: "eventList",      icon: FaList,          label: "Event List" },
              { to: "/dashboard/automation-report",menu: "automationReport",icon: FaChartBar,     label: "Automation Report" },
              { to: "/dashboard/upcoming-events",  menu: "upcomingEvents", icon: FaCalendarAlt,   label: "All Open Events" },
              { to: "/dashboard/events-history",   menu: "eventsHistory",  icon: FaHistory,       label: "Closed Events" },
              { to: "/dashboard/operators-list",   menu: "operatorsList",  icon: FaUserTie,       label: "Operators List" },
            ].map(({ to, menu, icon: Icon, label }) => {
              const isActive = activeMenu === menu;
              return (
                <Link
                  key={to}
                  to={to}
                  title={collapsed ? label : undefined}
                  className={`op-nav-link flex items-center gap-3 px-3 py-2.5 rounded-xl no-underline ${isActive ? "op-nav-active" : ""}`}
                >
                  <Icon style={{ color: isActive ? ACCENT : "rgba(226,232,240,0.38)", flexShrink: 0, fontSize: "0.95rem" }} />
                  {!collapsed && (
                    <span style={{ color: isActive ? ACCENT_SOFT : "rgba(226,232,240,0.7)" }} className="text-sm font-semibold truncate">
                      {label}
                    </span>
                  )}
                  {!collapsed && isActive && (
                    <span className="ml-auto w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0" />
                  )}
                </Link>
              );
            })}

            {/* Customers submenu */}
            <div>
              <button
                onClick={() => !collapsed && toggleMenu("customer")}
                title={collapsed ? "Customers" : undefined}
                className={`op-nav-link w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left ${
                  activeMenu === "customerList" || activeMenu === "addCustomer" ? "op-nav-active" : ""
                }`}
              >
                <FaUsers
                  style={{
                    color: activeMenu === "customerList" || activeMenu === "addCustomer"
                      ? ACCENT : "rgba(226,232,240,0.38)",
                    flexShrink: 0, fontSize: "0.95rem",
                  }}
                />
                {!collapsed && (
                  <>
                    <span
                      style={{
                        color: activeMenu === "customerList" || activeMenu === "addCustomer"
                          ? ACCENT_SOFT : "rgba(226,232,240,0.7)",
                      }}
                      className="text-sm font-semibold flex-1 truncate text-left"
                    >
                      Customers
                    </span>
                    <FaChevronDown
                      style={{
                        color: "rgba(226,232,240,0.35)",
                        transform: showSubMenu.customer ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                        fontSize: "0.65rem",
                      }}
                    />
                  </>
                )}
              </button>

              {showSubMenu.customer && !collapsed && (
                <div className="op-submenu-enter ml-4 mt-0.5 space-y-0.5 pl-3 border-l border-teal-400/20">
                  {[
                    { to: "/dashboard/customers-history", menu: "customerList", icon: RiListOrdered, label: "Customer List" },
                    { to: "/dashboard/add-customer",      menu: "addCustomer",  icon: RiUserAddFill,  label: "Add Customer" },
                  ].map(({ to, menu, icon: Icon, label }) => {
                    const isActive = activeMenu === menu;
                    return (
                      <Link
                        key={to}
                        to={to}
                        className={`op-nav-link flex items-center gap-2.5 px-3 py-2 rounded-xl no-underline ${isActive ? "op-nav-active" : ""}`}
                      >
                        <Icon style={{ color: isActive ? ACCENT : "rgba(226,232,240,0.35)", fontSize: "0.85rem", flexShrink: 0 }} />
                        <span style={{ color: isActive ? ACCENT_SOFT : "rgba(226,232,240,0.6)" }} className="text-xs font-semibold truncate">
                          {label}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* More links */}
            {[
              { to: "/dashboard/rewards-referrals", menu: "rewardsPage",  icon: FaGift,   label: "Rewards & Referrals" },
              { to: "/dashboard/wallet",            menu: "walletPage",   icon: FaWallet, label: "Wallet" },
              { to: "/dashboard/settings",          menu: "settingsPage", icon: FaCog,    label: "Settings" },
            ].map(({ to, menu, icon: Icon, label }) => {
              const isActive = activeMenu === menu;
              return (
                <Link
                  key={to}
                  to={to}
                  title={collapsed ? label : undefined}
                  className={`op-nav-link flex items-center gap-3 px-3 py-2.5 rounded-xl no-underline ${isActive ? "op-nav-active" : ""}`}
                >
                  <Icon style={{ color: isActive ? ACCENT : "rgba(226,232,240,0.38)", flexShrink: 0, fontSize: "0.95rem" }} />
                  {!collapsed && (
                    <span style={{ color: isActive ? ACCENT_SOFT : "rgba(226,232,240,0.7)" }} className="text-sm font-semibold truncate">
                      {label}
                    </span>
                  )}
                  {!collapsed && isActive && (
                    <span className="ml-auto w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Sidebar footer */}
          <div
            style={{ borderTop: "1px solid rgba(125,211,199,0.12)" }}
            className="px-3 py-3 shrink-0 space-y-2"
          >
            <div
              style={{ background: "rgba(20,184,166,0.08)", border: "1px solid rgba(20,184,166,0.16)" }}
              className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 overflow-hidden"
            >
              <div className="w-7 h-7 rounded-full bg-teal-400/12 border border-teal-400/20 flex items-center justify-center shrink-0">
                <FaUser className="text-teal-300 text-xs" />
              </div>
              {!collapsed && (
                <span className="text-gray-200 text-xs font-semibold truncate">
                  {orgName || "Enterprise"}
                </span>
              )}
            </div>

            <div className="flex gap-1.5">
              <button
                onClick={() => HandleLogout()}
                title="Logout"
                style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.22)" }}
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-red-400 hover:bg-red-500/20 transition text-xs font-bold"
              >
                <FaSignOutAlt />
                {!collapsed && "Logout"}
              </button>
              <Link
                to="/dashboard/settings"
                title="Settings"
                style={{ background: "rgba(20,184,166,0.08)", border: "1px solid rgba(20,184,166,0.18)" }}
                className={`flex items-center justify-center w-9 rounded-xl transition ${
                  activeMenu === "settingsPage" ? "text-teal-300" : "text-slate-400 hover:text-teal-300"
                }`}
              >
                <FaCog className="text-xs" />
              </Link>
            </div>
          </div>
        </aside>

        {/* ── Main area ────────────────────────────────────────────────────── */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Topbar */}
          <header
            style={{
              background: "#ffffff",
              borderBottom: "1px solid #dbe5ec",
              boxShadow: "0 8px 24px rgba(15,23,42,0.04)",
            }}
            className="flex items-center justify-between px-6 py-3 shrink-0"
          >
            <div className="flex items-center gap-3">
              <div className="w-1 h-6 rounded-full bg-teal-500 shrink-0" />
              <h1 className="text-xl font-black text-gray-800 tracking-tight">
                {headerName}
              </h1>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1 rounded-full bg-slate-100 p-1 ring-1 ring-slate-200">
                <span className="text-xs text-gray-500 pl-2 pr-1">Lang:</span>
                {["en", "ta"].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => handleLanguageChange(lang)}
                    className={`px-3 py-1.5 text-xs font-black rounded-full transition-all ${
                      language === lang
                        ? "bg-teal-600 text-white shadow-sm"
                        : "text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    {lang === "en" ? "EN" : "தமிழ்"}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 hidden sm:block">Typing Mode</span>
                  <button
                    onClick={() => setShowHelp(true)}
                    className="text-teal-600 hover:text-teal-700 transition-colors"
                    title="Keyboard Shortcuts Help"
                  >
                    <FaQuestionCircle size={16} />
                  </button>
                </div>
                <TypingModeSelector />
              </div>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto op-main-scroll bg-[linear-gradient(180deg,#eef4f8_0%,#f8fbfd_45%,#eef5f7_100%)]">
            <Outlet />
          </main>
        </div>
      </div>

      <CustomModal
        show={showHelp}
        onClose={() => setShowHelp(false)}
        title="Typing Guide"
        size="md"
      >
        <div className="p-1">
          <div className="bg-teal-50 border border-teal-100 rounded-xl p-4 mb-5 flex gap-4">
            <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center shrink-0">
              <FaKeyboard className="text-teal-600 text-xl" />
            </div>
            <div>
              <p className="text-sm font-bold text-teal-900 mb-1">How it works</p>
              <p className="text-xs text-teal-700 leading-relaxed">
                Use <b>Tanglish -&gt; Tamil</b> to type words like <b>vanakkam</b> and convert them on <b>Space</b> or <b>Enter</b>.
                Use <b>English</b> to keep plain English.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest pl-1">Tanglish Shortcuts</p>
            
            <div className="grid grid-cols-1 gap-3">
              {[
                { key: "Alt + J", desc: "Reopen suggestions later for the current translated word" },
                { key: "Alt + X", desc: "Cycle through similar letters (ந → ண → ன)" },
                { key: "Alt + 1", desc: "Choose 1st option (e.g., ந / ல / ர)" },
                { key: "Alt + 2", desc: "Choose 2nd option (e.g., ண / ள / ற)" },
                { key: "Alt + 3", desc: "Choose 3rd option (e.g., ன / ழ)" },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-200 group hover:border-teal-300 hover:bg-teal-50/30 transition-all">
                  <span className="text-sm font-semibold text-gray-700">{item.desc}</span>
                  <kbd className="px-2.5 py-1 rounded-lg bg-white border border-gray-300 text-teal-700 text-xs font-black shadow-sm group-hover:border-teal-400">
                    {item.key}
                  </kbd>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 rounded-xl border border-amber-100 bg-amber-50/50">
              <p className="text-[11px] font-bold text-amber-800 uppercase tracking-wider mb-2">Pro Tip</p>
              <p className="text-xs text-amber-700 leading-relaxed">
                Place your cursor <b>directly on or after</b> the word or letter you want to adjust before pressing the shortcut.
              </p>
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <button
              onClick={() => setShowHelp(false)}
              className="px-6 py-2.5 bg-teal-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-teal-700/20 hover:bg-teal-800 transition-all"
            >
              Start Typing
            </button>
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default OperatorDashboard;
